import { z } from "zod";

import type { BuilderModels } from "@/libs/builder/builder.models.types";
import { fetchOneBuilderEntry } from "@/libs/builder/tools/fetchOneBuilderEntry";
import { isDefinedLocale } from "@/libs/i18n/tools/isDefinedLocale";

interface MagentoStoreConfig {
  modelName: BuilderModels;
  mapPropertyName: string;
  defaultStore: string;
  isStoreSupported?: (store: string) => boolean;
  fallbackToLocale?: boolean;
}

export function createMagentoStoreHandler(config: MagentoStoreConfig) {
  const { defaultStore, modelName, mapPropertyName, isStoreSupported, fallbackToLocale = false } = config;

  return async function getStore(locale?: string): Promise<string> {
    if (!locale || !isDefinedLocale(locale)) {
      return defaultStore;
    }

    const content = await fetchOneBuilderEntry({
      model: modelName,
      locale,
    });

    const data = content?.data;
    const schema = z.object({
      [mapPropertyName]: z.record(z.string()),
    });

    if (!schema.safeParse(data).success) {
      return defaultStore;
    }

    const magentoStoreMap = data?.[mapPropertyName] ?? {};
    const selectedStore = magentoStoreMap[locale] || (fallbackToLocale ? locale : undefined);

    if (!selectedStore) {
      return defaultStore;
    }

    if (isStoreSupported && !isStoreSupported(selectedStore)) {
      if (process.env.NODE_ENV !== "test") {
        console.info(`Magento store "${selectedStore}" is not supported. Falling back to default store.`);
      }
      return defaultStore;
    }

    return selectedStore;
  };
}
